import { EzBadParamException } from '/ezlibrary/exceptions/EzBadParamException.js';

import {
    EzObject,
    EzString,
} from '/ezlibrary/helpers/EzHelpers.js';


import { EzJSONSerializable } from '/ezlibrary/entities/EzJSONSerializable.js';

/**
 * @class
 * @extends EzJSONSerializable
 * @description
 * TODO: Move this class to /ezlibrary/events/EzEventData.js and update import references.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ *
 * Data object that is sometimes available via the event object
 * passed to handlers by events triggered by EzEventEngine.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Parent class EzJsonSerializable provides the following:
 *  Static Method and Properties:
 *      1) ezLoadFromJSONObject(loadFromJsonObject, loadToJsonObject) (static method)
 *
 *  Instance Method and Properties
 *      1) ezToJSON(indentValue = 3, htmlDisplay = false) (instance method)
 *      2) ezFromJSONObject(jsonObject) (instance method)
 *      3) ezLoadFromJson(jsonString) (instance method)
 *      4) toJSON() (instance method)
 *      5) asJSON (instance read-only property)
 *      6) asJSONObject (instance read-only property)
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Example event object sent to handlers:
 * {
 *      // the reference to the object that triggered the event
 *      triggeredByRef: {any},
 *      // the reference to the object that triggered the event
 *      triggerOwner: {Object},
 *      // the string message for the event.
 *      message: {string},
 *      // Wrapper for the HTML element event
 *      data: {
 *          // The input element's onchange event object
 *          // See https://developer.mozilla.org/en-US/docs/Web/API/Event for more details on the properties and methods of the event object
 *          elementEvent: {
 *              // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *              // [Available Standard Properties]
 *              // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *              target: {
 *                  // The target property is a reference to the object to which the event was originally dispatched.
 *                  value: {string}
 *                      // The input elements value when changed
 *                  // ...other properties from the event target select input object...
 *              },
 *              currentTarget: {object},
 *                  // A reference to the currently registered target for the event.
 *                  // This is the object to which the event is currently slated to be sent.
 *                  // It's possible this has been changed along the way through retargeting.Returns the element whose event listeners triggered the event
 *              bubbles: {boolean},
 *                  // A read-only boolean value indicating whether the event is cancelable.
 *              cancelable: {boolean},
 *                  // A read-only boolean value indicating whether the event is cancelable.
 *              composed: {boolean},
 *                  // A readonly boolean indicating whether or not the event can bubble across the boundary between the shadow DOM and the regular DOM.
 *              eventPhase: {number},
 *                  // Indicates which phase of the event flow is being processed.
 *                  // It is one of the following numbers: NONE, CAPTURING_PHASE, AT_TARGET, BUBBLING_PHASE.
 *              defaultPrevented: {boolean},
 *                  // A boolean value that indicates whether or not the call to event.preventDefault() canceled the event.
 *              isTrusted: {boolean},
 *                  // A read-only boolean that indicates whether or not the event was initiated by the browser (after a user click, for instance) or
 *                  // by a script (using an event creation method, for example).
 *              timeStamp: {number},
 *                  // The time at which the event was created (in milliseconds). By specification, this value is time since epoch—but in reality,
 *                  // browsers' definitions vary. In addition, work is underway to change this to be a DOMHighResTimeStamp instead.
 *              type,
 *                  // Returns the name of the event
 *              // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *              // [Available Legacy/Deprecated Properties]
 *              // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *              srcElement: {object},
 *                 // An alias for the Event.target property.
 *                 // Legacy value, migrate to using the target property.
 *              originalTarget: {object},
 *                  // The original target of the event, before any retargetings.
 *                  // (legacy, read only, no migration target)
 *              cancelBubble: {boolean},
 *                  // Sets or returns whether the event should propagate up the hierarchy or not
 *                  // (legacy, Migrate to using the stopPropagation() function)
 *              returnValue: {any},
 *                  // A historical property still supported in order to ensure existing sites continue to work.
 *                  // (legacy, migrate to using the Event.preventDefault() and Event.defaultPrevented functions instead)
 *              scoped: {boolean},
 *                  // A boolean value indicating whether the given event will bubble across through the shadow root into the standard DOM.
 *                  // (legacy, read only, migrate to the composed property).
 *              explicitOriginalTarget: {object}
 *                 // The explicit original target of the event. (Non-standard, Read only)
 *              // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *              // [Available Functions]
 *              // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *              composedPath: {function},
 *                  // Returns the event's path
 *              preventDefault: {function},
 *                  // Cancels the event if it is cancelable, meaning that the default action that belongs to the event will not occur
 *              stopImmediatePropagation: {function},
 *                  // Prevents other listeners of the same event from being called
 *              stopPropagation(),
 *                  // Prevents further propagation of an event during event flow
 *          },
 *          // The HTML element id of the input element that triggered the event
 *          elementId: {string}
 *     }
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *      import { EzEventData } from '/ezlibrary/entities/EzEventData.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzEventData extends EzJSONSerializable {
    /**
     * @public @constructor
     * Creates a new instance of EzEventData
     *
     * @param {Object} triggeredByRef
     * @param {undefined|null|String} message
     * @param {undefined|null|*} data
     */
    constructor(triggeredByRef, message, data) {
        super();

        if (!EzObject.isValid(triggeredByRef)) {
            throw new EzBadParamException(
                'triggeredByRef',
                EzEventData,
                EzEventData.constructor);
        }

        this.triggeredByRef = triggeredByRef;

        this.message = message;

        this.data = data;
    }

    /**
     * @private @field
     * Stores the reference to the object that triggered the event
     * Default: null
     *
     * @type {any}
     */
    #triggeredByRef = null;
    /**
     * @public @property @getter
     * Gets the reference to the object that triggered the event
     * @returns {any}
     */
    get triggeredByRef() {
        if (null == triggerOwner) {
            throw new EzBadParamException(
                'triggeredByRef',
                this,
                `get EzEventDataInstance.triggeredByRef()`);
        }

        return this.#triggeredByRef;
    }
    /**
     * @public @property @setter
     * Sets the reference to the object that triggered the event
     *
     * @param {any}
     */
    set triggeredByRef(triggeredByRef) {
        this.#triggeredByRef = EzObject.assignOrNull(triggeredByRef);
    }
    /**
     * @public @readonly @property
     * Gets the reference to the object that triggered the event
     * @returns {Object}
     *
     * @deprecated
     * Migrate to: ezEventData.triggeredByRef property
     */
    get triggerOwner() {
        return this.#triggeredByRef;
    }

    /**
     * @private @field
     * Stores the string message for the event.
     * @type {undefined|null|String}
     */
    #message = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets the string message for the event.
     * @returns {undefined|null|String}
     */
    get message() {
        return this.#message;
    }
    /**
     * @public @property @getter
     * Sets the string message for the event.
     * @param {undefined|null|String}
     */
    set message(message) {
        this.#message = EzString.stringOrEmpty(message);
    }

    /**
     * @private @field
     * Stores any data associated with the event
     *
     * @type {any}
     */
    #data = null;
    /**
     * @public @property @getter
     * Gets any data associated with the event
     *
     * @returns {any}
     */
    get data() {
        return this.#data;
    }
    /**
     * @public @property @getter
     * Sets any data associated with the event
     * @param {any}
     */
    set data(data) {
        this.#data = EzObject.assignOrNull(data);
    }

    /**
     * @override
     * @public @readonly @property
     * Returns this instance as a JSON Object (will no longer have the getter/setter methods).
     * Useful for serailization to JSON.
     * @returns {object}
     */
    get asJSONObject() {
        let triggeredByName = EzString.stringOrNull(this.triggeredByRef);

        if (null == triggeredByName && EzObject.isValid(this.triggeredByRef)) {
            triggeredByName = this.triggeredByRef.constructor.name;
        }

        return {
            triggeredByRef: triggeredByName,
            message: this.message,
            data: EzObject.isValid(this.data)
                ? EzJson.toJson(this.data)
                : null
        };
    }

    /**
     * @override
     * @public @readonly @property
        Returns this instance as JSON string (no formatting)
     * @returns {String}
     */
    get asJSON() {
        return super.asJSON;
    }

    /**
     * @override
     * @public @method
     * Converts this instance into a JSON string with optional formatting.
     * @param {undefined|null|Number} indentValue
     * @param {undefined|null|Boolean} htmlDisplay
     * @returns {String}
     */
    ezToJSON(indentValue, htmlDisplay) {
        return super.ezToJSON(indentValue, htmlDisplay);
    }

    /**
     * @override
     * @public @method
     * Copies properties from the provided jsonObject to this instance (even if this instance does not define the property)
     * @param {Object} jsonObject
     * @returns {Object}
        Returns this instance with the key & values from the provided jsonObject.
     */
    ezFromJSONObject(jsonObject) {
        return super.ezFromJSONObject(jsonObject);
    }

    /**
     * @override
     * @public @method
     * Converts the provided jsonString to a JSON object and then
     * passes that object to ezFromJSONObject() to copies properties to this instance
     * (even if this instance does not define the property)
     * @param {String} jsonString
     * @returns {Object}
        Returns this instance with the key & values from the provided json string.
     */
    ezLoadFromJson(jsonString) {
        return super.ezLoadFromJson(jsonString);
    }
}
